import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { ArrowRight, CheckCircle, Palette, Shield, Wrench } from "lucide-react";
import { scrollToTop } from "../lib/utils";

interface Service {
  icon: React.ReactNode;
  title: string;
  description: string;
  image: string;
}

interface ProcessStep {
  number: string;
  title: string;
  description: string;
}

const Accueil: React.FC = () => {
  const services: Service[] = [
    {
      icon: <Palette className="h-8 w-8" />,
      title: "Marquage au sol",
      description:
        "Assurez une organisation optimale de vos espaces de stationnement avec des lignes claires et durables.",
      image: `${process.env.PUBLIC_URL}/photo-1558618666-fcd25c85cd64.jpg`,
    },
    {
      icon: <Shield className="h-8 w-8" />,
      title: "Pose de scellant",
      description:
        "La pose de scellant d'asphalte prolonge la durée de vie de la chaussée en protégeant contre les dommages causés par les intempéries, les rayons UV et les produits chimiques, tout en améliorant son apparence.",
      image: `${process.env.PUBLIC_URL}/photo-1600585154526-990dced4db0d.jpg`,
    },
    {
      icon: <Wrench className="h-8 w-8" />,
      title: "Réparation de fissures",
      description:
        "La réparation des fissures d'asphalte prévient l'infiltration d'eau, réduit les risques d'affaissement et prolonge la durabilité de la surface en évitant des dommages plus importants.",
      image: `${process.env.PUBLIC_URL}/photo-1504307651254-35680f356dfd.jpg`,
    },
  ];

  const processSteps: ProcessStep[] = [
    {
      number: "01",
      title: "Préparation",
      description:
        "La préparation de la surface est cruciale dans l'application d'un scellant bitumineux pour assurer une adhérence optimale. PaveMark comprend l'importance de cette étape et utilisent une combinaison d'outils manuels et mécanisés pour nettoyer en profondeur l'asphalte.",
    },
    {
      number: "02",
      title: "Pose de scellant à la main",
      description:
        "L'application à la main de scellant bitumineux est une étape cruciale pour assurer une protection optimale des surfaces asphaltées. Contrairement à l'application mécanique, l'application manuelle permet de cibler avec précision les zones critiques.",
    },
    {
      number: "03",
      title: "Marquage au sol",
      description:
        "Un marquage propre des lignes de stationnement est essentiel pour assurer une organisation efficace et sécuritaire dans les zones de stationnement. Des lignes claires maximisent l'utilisation de l'espace disponible.",
    },
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/40"></div>
          <img
            src={`${process.env.PUBLIC_URL}/photo-1486406146926-c627a92ad1ab.jpg`}
            alt="Parking lot with markings"
            className="w-full h-full object-cover"
          />
        </div>

        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          {/* <div className="w-24 h-24 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-8 shadow-2xl">
            <div className="w-16 h-16 bg-gray-900 rounded-full flex items-center justify-center">
              <div className="w-12 h-1 bg-white rounded-full"></div>
              <div className="w-8 h-1 bg-amber-400 rounded-full ml-1"></div>
            </div>
          </div> */}

          <p className="text-xl md:text-2xl text-amber-400 font-semibold mb-8">
            <img
              src={`${process.env.PUBLIC_URL}/pave-mark-logo.png`}
              alt="Parking lot with markings"
              className="w-25 h-25 object-cover text-center mx-auto"
            />
          </p>

          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
            <span className="bg-gradient-to-r from-white to-gray-200 bg-clip-text text-transparent">
              PaveMark
            </span>
          </h1>

          <p className="text-lg text-gray-200 mb-8 max-w-2xl mx-auto leading-relaxed">
            Spécialisés dans le marquage au sol, la pose de scellant d'asphalte
            et la réparation de fissures. Notre équipe jeune et dynamique met
            son expertise à votre service.
          </p>

          <Button
            asChild
            size="lg"
            className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white text-lg px-8 py-6 shadow-xl hover:shadow-2xl transition-all transform hover:scale-105"
          >
            <Link
              to="/contact"
              onClick={() => scrollToTop()}
              className="flex items-center gap-2"
            >
              Soumission gratuite
              <ArrowRight className="h-5 w-5" />
            </Link>
          </Button>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge
              variant="outline"
              className="mb-4 text-amber-600 border-amber-600"
            >
              Nos Services
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Excellence en marquage et protection d'asphalte
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Découvrez nos services spécialisés pour protéger et organiser vos
              espaces asphaltés
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service: Service, index: number) => (
              <Card
                key={index}
                className="group hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-0 shadow-lg overflow-hidden"
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                </div>
                <CardHeader className="pb-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-gradient-to-br from-amber-100 to-orange-100 rounded-lg text-amber-600 group-hover:from-amber-500 group-hover:to-orange-500 group-hover:text-white transition-all">
                      {service.icon}
                    </div>
                    <CardTitle className="text-xl group-hover:text-amber-600 transition-colors">
                      {service.title}
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-600 leading-relaxed">
                    {service.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge
              variant="outline"
              className="mb-4 text-amber-600 border-amber-600"
            >
              Comment nous travaillons
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Notre processus en 3 étapes
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Une méthode éprouvée pour garantir des résultats durables et de
              qualité
            </p>
          </div>

          <div className="space-y-12">
            {processSteps.map((step: ProcessStep, index: number) => (
              <div
                key={index}
                className={`flex flex-col ${
                  index % 2 === 0 ? "lg:flex-row" : "lg:flex-row-reverse"
                } items-center gap-12`}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-500 rounded-full flex items-center justify-center text-white text-xl font-bold shadow-lg">
                      {step.number}
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900">
                      {step.title}
                    </h3>
                  </div>
                  <p className="text-gray-600 leading-relaxed text-lg">
                    {step.description}
                  </p>
                  <div className="mt-6">
                    <CheckCircle className="h-5 w-5 text-green-500 inline mr-2" />
                    <span className="text-sm text-gray-600">
                      Processus certifié et garanti
                    </span>
                  </div>
                </div>

                <div className="flex-1">
                  <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8 border border-amber-100">
                    <div className="w-full h-64 bg-gradient-to-br from-amber-100 to-orange-100 rounded-xl flex items-center justify-center">
                      <div className="text-6xl text-amber-500 opacity-50 font-bold">
                        {step.number}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-amber-500 to-orange-500">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Prêt à commencer votre projet ?
          </h2>
          <p className="text-xl text-amber-100 mb-8 leading-relaxed">
            Contactez-nous dès aujourd'hui pour une soumission gratuite. Il nous
            fera plaisir de venir vous rencontrer.
          </p>
          <Button
            asChild
            size="lg"
            variant="outline"
            className="bg-white text-amber-600 border-white hover:bg-amber-50 text-lg px-8 py-6 shadow-xl"
          >
            <Link
              to="/contact"
              onClick={() => scrollToTop()}
              className="flex items-center gap-2"
            >
              Obtenir une soumission
              <ArrowRight className="h-5 w-5" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Accueil;
